import { UserRole } from '@atlas/types'
import { Navigate, Outlet, type RouteObject } from 'react-router-dom'
import { KitLayout } from '@/components/KitLayout'
import { Layout } from '@/components/Layout'
import { ProtectedRoute } from '@/components/ProtectedRoute'
import { ChecklistTemplateLibraryPage } from '@/pages/Admin/ChecklistTemplateLibraryPage'
import { PromptDefaultsPage } from '@/pages/Admin/PromptDefaults'
import { WorkflowBuilderPage } from '@/pages/Admin/WorkflowBuilderPage'
import { ArticlesPage } from '@/pages/Articles'
import { EditArticlePage } from '@/pages/Articles/edit'
import { NewArticlePage } from '@/pages/Articles/new'
import { AssetSheetPage } from '@/pages/AssetSheet'
import { AuditLogPage } from '@/pages/AuditLog'
import { AuthCallbackPage } from '@/pages/AuthCallback'
import { BoardPage } from '@/pages/Board'
import { CampaignsPage } from '@/pages/Campaigns'
import { CampaignDetailPage } from '@/pages/Campaigns/CampaignDetail'
import { EditCampaignPage } from '@/pages/Campaigns/edit'
import { NewCampaignPage } from '@/pages/Campaigns/new'
import {
	AvailabilityPage,
	AxisDetailPage,
	FullTaxonomyPage,
	MappingGridPage,
	RegistryPage,
	SystematicViewPage,
	ValueDetailPage,
} from '@/pages/Classification'
import { ContextValidatorPage } from '@/pages/ContextValidator'
import { DashboardPage } from '@/pages/Dashboard'
import { ExplorePage } from '@/pages/Explore'
import { BrandPage } from '@/pages/Explore/BrandPage'
import { ProductPage } from '@/pages/Explore/ProductPage'
import { FacetExplorerPage } from '@/pages/FacetExplorer'
import { FileProcessingDiagnosticsPage } from '@/pages/FileProcessingDiagnostics'
import { LibraryPage } from '@/pages/Library'
import { AssetDetailPage } from '@/pages/Library/AssetDetail'
import { LoginPage } from '@/pages/Login'
import { EditMessagePage, NewMessagePage } from '@/pages/Messages'
import { MessageDetailPage } from '@/pages/Messages/MessageDetail'
import { MessageListPage } from '@/pages/Messages/MessageList'
import { NotFoundPage } from '@/pages/NotFound'
import { PlaceholderPage } from '@/pages/Placeholder'
import { PromptListsPage } from '@/pages/PromptLists'
import { PromptsPage } from '@/pages/Prompts'
import { CreatePromptPage } from '@/pages/Prompts/CreatePrompt'
import { PromptDetailPage } from '@/pages/Prompts/PromptDetail'
import { RealtimeDiagnosticsPage } from '@/pages/RealtimeDiagnostics'
import { RulesPage } from '@/pages/Rules'
import { CreateRulePage } from '@/pages/Rules/CreateRule'
import { RuleDetailPage } from '@/pages/Rules/RuleDetail'
import { RuleValidationResultsPage } from '@/pages/Rules/RuleValidationResults'
import { SettingsPage } from '@/pages/Settings'
import { UploadClassifyPage } from '@/pages/UploadClassify'
import { UsersPage } from '@/pages/Users'
import { VeevaVaultDownloadPage } from '@/pages/VeevaVaultDownload'
import { WebhookDiagnosticsPage } from '@/pages/WebhookDiagnostics'
import { ResolverRulesPage, WorkflowDetailPage, WorkflowDiagnosticsPage, WorkflowListPage } from '@/pages/WorkflowAdmin'
import {
	WorkfrontBundleDetailPage,
	WorkfrontBundlesPage,
	WorkfrontErrorDetailPage,
	WorkfrontErrorsPage,
	WorkfrontMappingsPage,
	WorkfrontOverviewPage,
	WorkfrontPmRulesPage,
	WorkfrontRunDetailPage,
	WorkfrontRunsPage,
	WorkfrontSettingsPage,
	WorkfrontStreamsPage,
} from '@/pages/WorkfrontAdapter'
import { WorkOrderListPage } from '@/pages/WorkOrders'
import { EditWorkOrderPage, NewWorkOrderPage } from '@/pages/WorkOrders/new'
import { WorkOrderStagePage } from '@/pages/WorkOrdersV2'

export const routes: RouteObject[] = [
	{
		path: '/login',
		element: <LoginPage />,
	},
	{
		// Must be outside ProtectedRoute — this page handles the OAuth redirect
		// before a token exists in sessionStorage.
		path: '/auth/callback',
		element: <AuthCallbackPage />,
	},
	{
		// The work-order screens the Full Path redraws sit in the UI Kit's frame (ADR-0044);
		// every other route keeps the legacy Layout below until its screen migrates.
		path: '/work-orders/:id',
		element: (
			<ProtectedRoute>
				<KitLayout />
			</ProtectedRoute>
		),
		children: [{ index: true, element: <WorkOrderStagePage /> }],
	},
	{
		path: '/',
		element: (
			<ProtectedRoute>
				<Layout />
			</ProtectedRoute>
		),
		children: [
			{
				index: true,
				element: <DashboardPage />,
			},
			{
				path: 'board',
				element: <BoardPage />,
			},
			{
				path: 'work-orders',
				element: <WorkOrderListPage key="children" />,
			},
			{
				path: 'work-orders/briefs',
				element: <WorkOrderListPage key="briefs" briefs />,
			},
			{
				path: 'work-orders/new',
				element: (
					<ProtectedRoute minRole={UserRole.USER}>
						<NewWorkOrderPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'work-orders/:id/edit',
				element: (
					<ProtectedRoute minRole={UserRole.USER}>
						<EditWorkOrderPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'assets',
				element: <LibraryPage />,
			},
			{
				path: 'assets/:id',
				element: <AssetDetailPage />,
			},
			{
				path: 'upload',
				element: (
					<ProtectedRoute minRole={UserRole.USER}>
						<UploadClassifyPage />
					</ProtectedRoute>
				),
			},
			{
				path: 'settings',
				element: <SettingsPage />,
			},
			// Explore — guided funnel + Brand and Product pages.
			{ path: 'explore', element: <ExplorePage /> },
			{ path: 'explore/brand/:brandId', element: <BrandPage /> },
			{ path: 'explore/product/:productId', element: <ProductPage /> },

			// Modules not yet built — navigable placeholders.
			// Campaigns — minimum role USER, matching the backend's read-access
			// contract; VIEWER is excluded. `new` is ordered ahead of `:campaignId`
			// so it never resolves as a Campaign id.
			{
				element: (
					<ProtectedRoute minRole={UserRole.USER}>
						<Outlet />
					</ProtectedRoute>
				),
				children: [
					{ path: 'campaigns', element: <CampaignsPage /> },
					{ path: 'campaigns/new', element: <NewCampaignPage /> },
					{ path: 'campaigns/:campaignId/edit', element: <EditCampaignPage /> },
					{ path: 'campaigns/:campaignId', element: <CampaignDetailPage /> },
				],
			},
			// Articles — reading is open to every authenticated role (VIEWER
			// included), matching the API; authoring needs USER, so the composer
			// routes carry their own gate below. There is deliberately no
			// `articles/:articleId` reader: the edit route is the author's detail
			// surface and the Brand details tab is the reader's.
			{ path: 'articles', element: <ArticlesPage /> },
			{
				element: (
					<ProtectedRoute minRole={UserRole.USER}>
						<Outlet />
					</ProtectedRoute>
				),
				// `new` first, so the literal segment never resolves as an article id.
				children: [
					{ path: 'articles/new', element: <NewArticlePage /> },
					{ path: 'articles/:articleId/edit', element: <EditArticlePage /> },
				],
			},
			// Rules Manager (design §8.13) — explorer, create, detail
			{ path: 'rules', element: <RulesPage /> },
			// Typing the URL is the other way in, and the button being refused says
			// nothing about the route behind it.
			{
				path: 'rules/new',
				element: (
					<ProtectedRoute minRole={UserRole.ADMIN}>
						<CreateRulePage />
					</ProtectedRoute>
				),
			},
			{ path: 'rules/validation-results', element: <RuleValidationResultsPage /> },
			{ path: 'rules/:id', element: <RuleDetailPage /> },
			// Literal before param, so `/prompts/new` is not captured as an id.
			{ path: 'prompts', element: <PromptsPage /> },
			{ path: 'prompts/new', element: <CreatePromptPage /> },
			{ path: 'prompts/:id', element: <PromptDetailPage /> },
			{ path: 'prompt-lists', element: <PromptListsPage /> },
			{ path: 'messages', element: <MessageListPage /> },
			{
				element: (
					<ProtectedRoute minRole={UserRole.USER}>
						<Outlet />
					</ProtectedRoute>
				),
				children: [
					{ path: 'messages/new', element: <NewMessagePage /> },
					{ path: 'messages/:id/edit', element: <EditMessagePage /> },
				],
			},
			{ path: 'messages/:id', element: <MessageDetailPage /> },
			{
				path: 'admin',
				element: (
					<ProtectedRoute minRole={UserRole.ADMIN}>
						<PlaceholderPage />
					</ProtectedRoute>
				),
			},

			// Classification administration. The frontend guard prevents route
			// access while the backend remains authoritative.
			{
				element: (
					<ProtectedRoute minRole={UserRole.ADMIN}>
						<Outlet />
					</ProtectedRoute>
				),
				children: [
					{ path: 'admin/classification', element: <RegistryPage /> },
					{ path: 'admin/audit', element: <AuditLogPage /> },
					{ path: 'admin/classification/systematic', element: <SystematicViewPage /> },
					{ path: 'admin/classification/full', element: <FullTaxonomyPage /> },
					{ path: 'admin/classification/axes/:axisId', element: <AxisDetailPage /> },
					{ path: 'admin/classification/axes/:axisId/values/:valueId', element: <ValueDetailPage /> },
					// Kept so links already in the wild still resolve; both are tabs on the
					// value page now.
					{ path: 'admin/classification/values/:valueId/mappings', element: <MappingGridPage /> },
					{ path: 'admin/classification/values/:valueId/availability', element: <AvailabilityPage /> },
					// Workflow administration. `resolver-rules`, `diagnostics`, and
					// `builder` are ordered ahead of `:workflowId` so none resolves as a
					// workflow id.
					{ path: 'admin/workflows', element: <WorkflowListPage /> },
					{ path: 'admin/workflows/resolver-rules', element: <ResolverRulesPage /> },
					{ path: 'admin/prompt-defaults', element: <PromptDefaultsPage /> },
					{ path: 'admin/workflows/diagnostics', element: <WorkflowDiagnosticsPage /> },
					{ path: 'admin/workflows/builder', element: <WorkflowBuilderPage /> },
					{ path: 'admin/workflows/:workflowId', element: <WorkflowDetailPage /> },
					{ path: 'admin/checklist-templates', element: <ChecklistTemplateLibraryPage /> },
					{ path: 'admin/veeva-vault', element: <VeevaVaultDownloadPage /> },
					{ path: 'admin/asset-sheet', element: <AssetSheetPage /> },
					// The Workfront adapter, ADMIN like every route it calls.
					{ path: 'admin/workfront', element: <WorkfrontOverviewPage /> },
					{ path: 'admin/workfront/bundles', element: <WorkfrontBundlesPage /> },
					{ path: 'admin/workfront/bundles/:id', element: <WorkfrontBundleDetailPage /> },
					{ path: 'admin/workfront/mappings', element: <Navigate to="/admin/workfront/mappings/fields" replace /> },
					{ path: 'admin/workfront/mappings/:tab', element: <WorkfrontMappingsPage /> },
					{ path: 'admin/workfront/pm-rules', element: <WorkfrontPmRulesPage /> },
					{ path: 'admin/workfront/runs', element: <WorkfrontRunsPage /> },
					{ path: 'admin/workfront/runs/:id', element: <WorkfrontRunDetailPage /> },
					{ path: 'admin/workfront/streams', element: <WorkfrontStreamsPage /> },
					{ path: 'admin/workfront/errors', element: <WorkfrontErrorsPage /> },
					{ path: 'admin/workfront/errors/:id', element: <WorkfrontErrorDetailPage /> },
					{ path: 'admin/workfront/settings', element: <WorkfrontSettingsPage /> },
				],
			},
			// SUPER_ADMIN-only administration (UX gate; the backend remains
			// authoritative and returns 403 for anyone else). Realtime diagnostics sits
			// here rather than under ADMIN because it exposes deployment coordinates and
			// the live message stream for the signed-in user.
			{
				element: (
					<ProtectedRoute minRole={UserRole.SUPER_ADMIN}>
						<Outlet />
					</ProtectedRoute>
				),
				children: [
					{ path: 'admin/users', element: <UsersPage /> },
					{ path: 'admin/realtime', element: <RealtimeDiagnosticsPage /> },
					{ path: 'admin/file-processing', element: <FileProcessingDiagnosticsPage /> },
					{ path: 'admin/webhooks', element: <WebhookDiagnosticsPage /> },
					{ path: 'admin/classification/facet-explorer', element: <FacetExplorerPage /> },
					{ path: 'admin/classification/context-validator', element: <ContextValidatorPage /> },
				],
			},
		],
	},
	{
		path: '*',
		element: <NotFoundPage />,
	},
]
